import { useEffect, useMemo, useState } from 'react';
import { FaArrowLeft, FaBuilding, FaChartLine, FaChevronLeft, FaChevronRight, FaFileInvoiceDollar, FaHome, FaLayerGroup, FaListAlt, FaMoon, FaPaintBrush, FaSlidersH, FaSun, FaTasks, FaUserFriends, FaUserShield, FaUsers } from 'react-icons/fa';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import AccessDenied from './components/AccessDenied';
import SessionExpiredModal from './components/SessionExpiredModal';
import SidebarUserProfile from './components/SidebarUserProfile';
import WhatsNewModal from './components/WhatsNewModal';
import { API_BASE_URL, SELECT_TENANT_URL } from './config';
import { BILLING_ENABLED } from './constants/featureFlags';
import { PERMISSIONS } from './constants/permissions';
import { useAuth } from './context/AuthContext';
import { usePermissions } from './context/PermissionsContext';
import { useTenant } from './context/TenantContext';
import { useTheme } from './context/ThemeContext';
import './Layout.css';
import { fetchWithAuth } from './utils/fetchWithAuth';
import { clearAuthSession, filterTenantsForFlowAccess } from './utils/authSession';
import { getApiErrorMessage } from './utils/apiErrorMessage';

function Layout({ children }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
    const [logoUrl, setLogoUrl] = useState('');
    const [expandedSection, setExpandedSection] = useState('management');
    const [tenantLoading, setTenantLoading] = useState(false);
    const [tenantError, setTenantError] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
    const location = useLocation();
    const navigate = useNavigate();
    const cookies = new Cookies();
    const { currentTenant, tenants, setCurrentTenant, setTenants, refreshTenants } = useTenant();
    const { hasRole, hasPermission, isPermissionsLoading, clearPermissions, tenantData } = usePermissions();
    const { isSessionExpired, clearSessionExpired } = useAuth();
    const { isDark, toggleTheme, darkModeEnabled } = useTheme();
    const canViewBilling = hasPermission(PERMISSIONS.BILLING_READ);
    const selectableTenants = useMemo(() => {
        const filtered = filterTenantsForFlowAccess(tenants, tenantData);
        if (currentTenant && !filtered.some((tenant) => String(tenant.id) === String(currentTenant.id))) {
            return [currentTenant, ...filtered];
        }
        return filtered;
    }, [tenants, tenantData, currentTenant]);

    useEffect(() => {
        const fetchLogoUrl = async () => {
            const cached = sessionStorage.getItem('app:logoUrl');
            if (cached) {
                const resolved = (!cached || cached === '/HeaderLogo.png' || cached === '/favicon.ico')
                    ? '/WandawaziFlowIcon.png'
                    : cached;
                setLogoUrl(resolved);
                return;
            }
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/get_settings`);

                if (!response.ok) {
                    console.error(`HTTP error! Status: ${response.status}`);
                    setLogoUrl('/WandawaziFlowIcon.png');
                    return;
                }

                const settings = await response.json();
                const logoSetting = settings.find(setting => setting.SettingName === 'logoUrl');
                let url = logoSetting?.SettingValue || '/WandawaziFlowIcon.png';
                if (!url || url === '/HeaderLogo.png' || url === '/favicon.ico') url = '/WandawaziFlowIcon.png';
                sessionStorage.setItem('app:logoUrl', url);
                setLogoUrl(url);
            } catch (error) {
                console.error('Error fetching logo URL:', error);
                setLogoUrl('/WandawaziFlowIcon.png');
            }
        };

        fetchLogoUrl();
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleSidebar = () => {
        setCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebarCollapsed', String(next));
            return next;
        });
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const pageTitleMap = {
        "/home": "Dashboard",
        "/care-recipients": "Service Recipients",
        "/activities": "Activities",
        "/priceList": "Price Lists",
        "/staff-demand-report": "Staff Demand Report",
        "/user-management": "User Management",
        "/staff-prices": "Staff Prices",
        "/branding": "Branding",
        ...(BILLING_ENABLED && {
            "/billing": "Billing",
            "/gl-accounts": "Billing Setup",
            "/billing-groups": "Billing Setup",
            "/billing-sheets": "Billing Setup",
        }),
    };

    const pageTitle = pageTitleMap[location.pathname]
        || (BILLING_ENABLED && location.pathname.startsWith('/billing/') ? 'Billing' : 'Dashboard');

    const handleLogout = () => {
        clearAuthSession();
        clearPermissions();
        clearSessionExpired();
        navigate('/');
    };

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    useEffect(() => {
        const switchedTenant = sessionStorage.getItem('tenantSwitchSuccess');
        if (switchedTenant) {
            sessionStorage.removeItem('tenantSwitchSuccess');
            setToast({ show: true, message: `Successfully switched to ${switchedTenant}`, variant: 'success' });
        }
    }, []);

    useEffect(() => {
        setTenantError(null);
        if (location.pathname === '/user-management') {
            setExpandedSection('admin');
        }
    }, [location.pathname]);

    // Handle tenant switching
    const handleTenantSelect = async (tenantId) => {
        if (!tenantId || String(tenantId) === String(currentTenant?.id)) return;

        setTenantError(null);
        setTenantLoading(true);
        try {
            const response = await fetchWithAuth(`${SELECT_TENANT_URL}`, {
                method: 'POST',
                body: JSON.stringify({ tenantId }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('TenantSelector: Error response:', errorText);
                throw new Error(getApiErrorMessage(errorText, 'Could not switch organisation.'));
            }

            const data = await response.json();

            // Try multiple possible field names for the new token
            const newToken = data.token || data.accessToken || data.jwt || data.jwtToken;

            if (!newToken) {
                console.error('TenantSelector: No token in response. Available fields:', Object.keys(data));
                throw new Error('No token returned from select-tenant');
            }

            // Store new token in localStorage and cookies
            const expiresIn = Number(data.expiresIn) > 0 ? Number(data.expiresIn) : 3600;
            localStorage.setItem('token', newToken);
            cookies.set('token', newToken, { path: '/', maxAge: expiresIn });
            // Always refresh expiry metadata so proactive refresh stays accurate after tenant switch
            localStorage.setItem('tokenExpiresAt', String(Date.now() + expiresIn * 1000));
            if (data.refreshToken) {
                localStorage.setItem('refreshToken', data.refreshToken);
            }

            // Update tenant context with activeTenant from response
            if (data.activeTenant) {
                setCurrentTenant(data.activeTenant);
                // Persist the selected tenant to localStorage
                localStorage.setItem('currentTenant', JSON.stringify(data.activeTenant));
            } else {
                // Fallback: create and save tenant object from the tenantId
                const selectedTenant = selectableTenants.find(t => t.id === tenantId);
                if (selectedTenant) {
                    localStorage.setItem('currentTenant', JSON.stringify(selectedTenant));
                }
            }

            // Refresh tenants from API to get latest list
            await refreshTenants();

            const selectedTenant = selectableTenants.find(t => t.id === tenantId);
            const displayName = selectedTenant ? selectedTenant.name : tenantId;
            sessionStorage.setItem('tenantSwitchSuccess', displayName);

            const isTenantSpecificPage = /\/(careplans|careplan|quotation)\//.test(location.pathname);
            if (isTenantSpecificPage) {
                window.location.href = '/home';
            } else {
                window.location.reload();
            }

        } catch (error) {
            console.error('Error switching tenant:', error);
            setTenantError(error.message);
        } finally {
            setTenantLoading(false);
        }
    };

    const navigationSections = [
        {
            id: 'management',
            title: 'Management',
            icon: <FaUsers />,
            items: [
                {
                    title: "Service Recipients",
                    icon: <FaUserFriends />,
                    link: "/care-recipients",
                    description: "Manage service recipients"
                },
                ...(BILLING_ENABLED && canViewBilling ? [{
                    title: "Billing",
                    icon: <FaFileInvoiceDollar />,
                    link: "/billing",
                    description: "Monthly billing worksheets"
                }] : []),
            ]
        },
        {
            id: 'configuration',
            title: 'Configuration',
            icon: <FaSlidersH />,
            items: [
                {
                    title: "Activities",
                    icon: <FaTasks />,
                    link: "/activities",
                    description: "View and manage activities"
                },
                {
                    title: "Price Lists",
                    icon: <FaListAlt />,
                    link: "/priceList",
                    description: "Manage pricing items"
                },
                {
                    title: "Staff Prices",
                    icon: <FaUsers />,
                    link: "/staff-prices",
                    description: "Staff pricing and rates"
                },
                {
                    title: "Branding",
                    icon: <FaPaintBrush />,
                    link: "/branding",
                    description: "Quotation PDF logo & colors"
                },
                ...(BILLING_ENABLED && canViewBilling ? [{
                    title: "Billing Setup",
                    icon: <FaLayerGroup />,
                    link: "/gl-accounts",
                    description: "GL accounts & billing groups"
                }] : []),
            ]
        },
        {
            id: 'reports',
            title: 'Insights',
            icon: <FaChartLine />,
            items: [
                {
                    title: "Staff Demand Report",
                    icon: <FaChartLine />,
                    link: "/staff-demand-report",
                    description: "Generate demand reports"
                }
            ]
        },
        ...(hasRole('Admin') ? [{
            id: 'admin',
            title: 'Admin',
            icon: <FaUserShield />,
            items: [
                {
                    title: "User Management",
                    icon: <FaUsers />,
                    link: "/user-management",
                    description: "Roles and access"
                }
            ]
        }] : []),
    ];

    const handleSessionExpiredLogin = () => {
        clearSessionExpired();
        handleLogout();
    };

    return (
        <div className={`layout-container ${collapsed ? 'collapsed' : ''}`}>
            {isSessionExpired && <SessionExpiredModal onLogIn={handleSessionExpiredLogin} />}
            <WhatsNewModal />
            {/* Modern Sidebar */}
            <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
                {/* Sidebar Header */}
                <div className="sidebar-header">
                    <div className="logo-section">
                        <img
                            src={logoUrl || '/WandawaziFlowIcon.png'}
                            alt="Wandawazi Flow"
                            className={`sidebar-logo ${collapsed ? 'sidebar-logo--icon' : 'sidebar-logo--full'}`}
                            onError={(e) => { e.target.src = '/WandawaziFlowIcon.png'; }}
                        />
                        <div className={`brand-name ${collapsed ? 'brand-name--hidden' : ''}`}>
                            <span className="brand-name__title">Wandawazi</span>
                            <span className="brand-name__subtitle">Flow</span>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav className="sidebar-nav">
                    <div className="nav-sections">
                        <Link
                            to="/home"
                            className={`nav-item nav-item--standalone ${isActive('/home') ? 'active' : ''}`}
                            title={collapsed ? 'Dashboard' : undefined}
                        >
                            <div className="nav-item-icon"><FaHome /></div>
                            <div className={`nav-item-content ${collapsed ? 'nav-item-content--hidden' : ''}`}>
                                <span className="nav-item-title">Dashboard</span>
                                <span className="nav-item-description">Overview and analytics</span>
                            </div>
                        </Link>
                        {navigationSections.map((section) => (
                            <div key={section.id} className="nav-section">
                                <div
                                    className={`section-header ${collapsed ? 'section-header--hidden' : ''}`}
                                    onClick={() => !collapsed && toggleSection(section.id)}
                                    aria-hidden={collapsed}
                                >
                                    <div className="section-icon">{section.icon}</div>
                                    <span className="section-title">{section.title}</span>
                                    <FaChevronRight
                                        className={`section-arrow ${expandedSection === section.id ? 'expanded' : ''}`}
                                    />
                                </div>

                                <div className={`section-items ${expandedSection === section.id || collapsed ? 'expanded' : ''}`}>
                                    {section.items.map((item, index) => (
                                        <Link
                                            key={index}
                                            to={item.link}
                                            className={`nav-item ${isActive(item.link) ? 'active' : ''}`}
                                            title={collapsed ? item.title : undefined}
                                        >
                                            <div className="nav-item-icon">{item.icon}</div>
                                            <div className={`nav-item-content ${collapsed ? 'nav-item-content--hidden' : ''}`}>
                                                <span className="nav-item-title">{item.title}</span>
                                                <span className="nav-item-description">{item.description}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </nav>

                <div className="sidebar-dock">
                    <div className="sidebar-dock-actions">
                        <button
                            type="button"
                            className="sidebar-dock-link"
                            onClick={toggleSidebar}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {collapsed ? <FaChevronRight className="sidebar-dock-icon" /> : <FaChevronLeft className="sidebar-dock-icon" />}
                            {!collapsed && <span>Collapse</span>}
                        </button>
                    </div>
                    <SidebarUserProfile collapsed={collapsed} onLogout={handleLogout} />
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Top Navigation Bar */}
                <header className="top-nav">
                    <div className="top-nav-left">
                        <button
                            className="back-button"
                            onClick={() => {
                                if (location.pathname.split("/").filter(Boolean).length === 1) {
                                    navigate("/home");
                                } else {
                                    navigate(-1);
                                }
                            }}
                            style={{ display: location.pathname === "/" ? 'none' : 'flex' }}
                        >
                            <FaArrowLeft />
                        </button>

                        {/* Tenant Selector Dropdown - Always show for debugging */}
                        <div className="tenant-selector">
                            <FaBuilding className="tenant-icon" />
                            <select
                                value={currentTenant?.id || ''}
                                onChange={(e) => handleTenantSelect(e.target.value)}
                                disabled={tenantLoading || !selectableTenants?.length}
                                className="tenant-dropdown"
                            >
                                <option value="" disabled>
                                    {currentTenant?.name || (selectableTenants?.length ? 'Select Tenant' : 'Loading...')}
                                </option>
                                {selectableTenants && selectableTenants.map((tenant) => (
                                    <option key={tenant.id} value={tenant.id}>
                                        {tenant.name}
                                    </option>
                                ))}
                            </select>
                            {tenantLoading && <span className="tenant-loading">Switching...</span>}
                        </div>

                        <h1 className="page-title">{pageTitle}</h1>
                    </div>

                    <div className="top-nav-right">
                        {darkModeEnabled && (
                            <button
                                type="button"
                                className="theme-toggle-btn"
                                onClick={toggleTheme}
                                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={isDark ? 'Light mode' : 'Dark mode'}
                            >
                                {isDark ? <FaSun /> : <FaMoon />}
                            </button>
                        )}
                        {/* <button className="nav-icon-btn" title="Notifications">
                            <FaBell />
                        </button>
                        <div className="user-menu">
                            <button className="nav-icon-btn" onClick={toggleMenu} title="User Menu">
                                <FaUser />
                            </button>
                            {menuOpen && (
                                <div className="dropdown-menu">
                                    <button onClick={handleLogout} className="dropdown-item">
                                        <FaSignOutAlt /> Logout
                                    </button>
                                </div>
                            )}
                        </div> */}
                    </div>
                </header>

                {/* Page Content */}
                <div className="page-content">
                    {tenantError ? (
                        <AccessDenied
                            message={tenantError}
                            onGoHome={tenants?.length > 1 ? () => { setTenantError(null); navigate('/home'); } : handleLogout}
                            primaryLabel={tenants?.length > 1 ? 'Return to Dashboard' : 'Logout'}
                            hideGoBack={!(tenants?.length > 1)}
                            onGoBack={tenants?.length > 1 ? () => setTenantError(null) : undefined}
                        />
                    ) : (
                        <Outlet context={{ collapsed, setCollapsed }} />
                    )}
                </div>
            </main>
        {toast.show && <div className={`app-toast app-toast-${toast.variant}`}>{toast.message}</div>}
        </div>
    );
}

export default Layout;